import {
  vertSrc,
  makeFragSrc,
  makePrepassFragSrc,
  makePostFragSrc,
  createUniforms,
  setUniforms,
  createPrepassUniforms,
  setPrepassUniforms,
  createPostUniforms,
  setPostUniforms,
  mobileDefaults,
} from './gradient.js'
import { hexToRgb } from './color.js'
import { tick } from './animation.js'
import { types } from './config.js'

const FBO_SCALE = 0.25
const BLUR_RADIUS = 1.5

const HEX_RE = /^#[0-9a-f]{6}$/i
const parseColor = (c) =>
  typeof c === 'string' && HEX_RE.test(c) ? hexToRgb(c) : null

export function initCanvas(canvas, type = 'onboarding', opts = {}) {
  const { motion = true, onFail, primaryColor, secondaryColor } = opts
  const primaryRgb = parseColor(primaryColor)
  const secondaryRgb = parseColor(secondaryColor)

  if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
    console.error('initCanvas: canvas must be an HTMLCanvasElement')
    return () => {}
  }
  if (!types[type]) {
    console.error(`initCanvas: unknown type "${type}"`)
    return () => {}
  }

  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')
  if (reducedMotion?.matches) {
    onFail?.()
    return () => {}
  }
  const onReducedChange = (e) => {
    if (e.matches) {
      onFail?.()
    }
  }
  reducedMotion?.addEventListener?.('change', onReducedChange)

  const gl = canvas.getContext('webgl', { antialias: true })
  if (!gl) {
    onFail?.()
    reducedMotion?.removeEventListener?.('change', onReducedChange)
    return () => {}
  }

  if (!gl.getExtension('OES_standard_derivatives')) {
    console.warn(
      'OES_standard_derivatives unavailable; ridge AA may fail to compile',
    )
  }

  const halfFloatExt = gl.getExtension('OES_texture_half_float')
  const halfFloatLinearExt = gl.getExtension('OES_texture_half_float_linear')
  const fboTexType =
    halfFloatExt && halfFloatLinearExt
      ? halfFloatExt.HALF_FLOAT_OES
      : gl.UNSIGNED_BYTE
  if (fboTexType === gl.UNSIGNED_BYTE) {
    console.warn(
      'OES_texture_half_float[_linear] unavailable; noise FBO stays at 8-bit, warp will show quantisation steps',
    )
  }

  let dirty = true
  const state = new Proxy(
    { ...mobileDefaults, animMoveEnabled: motion ? 1 : 0 },
    {
      set(obj, key, val) {
        if (obj[key] !== val) {
          obj[key] = val
          dirty = true
        }
        return true
      },
    },
  )

  let mainProgram = null
  let prepassProgram = null
  let postProgram = null
  let mainUniforms
  let prepassUniforms
  let postUniforms

  function compileShader(src, shaderType) {
    const s = gl.createShader(shaderType)
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(s))
      gl.deleteShader(s)
      return null
    }
    return s
  }

  function linkProgram(vs, fs) {
    const p = gl.createProgram()
    gl.attachShader(p, vs)
    gl.attachShader(p, fs)
    gl.linkProgram(p)
    gl.deleteShader(vs)
    gl.deleteShader(fs)
    return p
  }

  function buildPrograms(octaves) {
    if (mainProgram) gl.deleteProgram(mainProgram)
    if (prepassProgram) gl.deleteProgram(prepassProgram)

    const mvs = compileShader(vertSrc, gl.VERTEX_SHADER)
    const mfs = compileShader(makeFragSrc(), gl.FRAGMENT_SHADER)
    mainProgram = linkProgram(mvs, mfs)
    mainUniforms = createUniforms(gl, mainProgram)

    const pvs = compileShader(vertSrc, gl.VERTEX_SHADER)
    const pfs = compileShader(makePrepassFragSrc(octaves), gl.FRAGMENT_SHADER)
    prepassProgram = linkProgram(pvs, pfs)
    prepassUniforms = createPrepassUniforms(gl, prepassProgram)

    if (!postProgram) {
      const postVs = compileShader(vertSrc, gl.VERTEX_SHADER)
      const postFs = compileShader(makePostFragSrc(), gl.FRAGMENT_SHADER)
      postProgram = linkProgram(postVs, postFs)
      postUniforms = createPostUniforms(gl, postProgram)
    }
  }

  function useProgram(program) {
    gl.useProgram(program)
    const aPos = gl.getAttribLocation(program, 'a_position')
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0)
  }

  const quad = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1])
  const buf = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW)

  let fbo = null
  let fboTex = null
  let fboWidth = 0
  let fboHeight = 0

  function ensureFBO(canvasW, canvasH) {
    const w = Math.max(16, Math.floor(canvasW * FBO_SCALE))
    const h = Math.max(16, Math.floor(canvasH * FBO_SCALE))
    if (w === fboWidth && h === fboHeight && fbo) return
    fboWidth = w
    fboHeight = h
    if (fboTex) gl.deleteTexture(fboTex)
    if (fbo) gl.deleteFramebuffer(fbo)
    fboTex = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, fboTex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, fboTexType, null)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    fbo = gl.createFramebuffer()
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      fboTex,
      0,
    )
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  }

  let colorFbo = null
  let colorTex = null
  let colorFboWidth = 0
  let colorFboHeight = 0

  function ensureColorFBO(canvasW, canvasH) {
    if (canvasW === colorFboWidth && canvasH === colorFboHeight && colorFbo)
      return
    colorFboWidth = canvasW
    colorFboHeight = canvasH
    if (colorTex) gl.deleteTexture(colorTex)
    if (colorFbo) gl.deleteFramebuffer(colorFbo)
    colorTex = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, colorTex)
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      canvasW,
      canvasH,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      null,
    )
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    colorFbo = gl.createFramebuffer()
    gl.bindFramebuffer(gl.FRAMEBUFFER, colorFbo)
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      colorTex,
      0,
    )
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  }

  function pickConfig(w) {
    for (const [widthKey, config] of types[type]) {
      if (w < widthKey) return config
    }
    return types[type][types[type].length - 1][1]
  }

  let currentWidthKey = null
  let currentOctaves = null

  function applyConfigForWidth(w) {
    const config = pickConfig(w)
    let matchedKey = null
    for (const [widthKey] of types[type]) {
      if (w < widthKey) {
        matchedKey = widthKey
        break
      }
    }
    const tierChanged = matchedKey !== currentWidthKey
    currentWidthKey = matchedKey

    const move = state.animMoveEnabled
    const animTime = state.animTime
    for (const key of Object.keys(config)) state[key] = config[key]
    state.animMoveEnabled = move
    state.animTime = animTime
    if (primaryRgb) state.colorStop1 = primaryRgb
    if (secondaryRgb) {
      state.colorStop0 = secondaryRgb
      state.secondaryColor = secondaryRgb
    }

    if (tierChanged || state.noiseOctaves !== currentOctaves) {
      buildPrograms(state.noiseOctaves)
      currentOctaves = state.noiseOctaves
    }
    dirty = true
  }

  let lastResizeW = 0
  let lastResizeH = 0
  function resize() {
    const w = window.innerWidth
    const h = window.innerHeight

    if (w === lastResizeW && Math.abs(h - lastResizeH) < 150) return
    lastResizeW = w
    lastResizeH = h
    const tierCap = w < 1025 ? 2 : 1.5
    const dpr = Math.min(window.devicePixelRatio || 1, tierCap)
    canvas.style.width = w + 'px'
    canvas.style.height = h + 'px'
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
    applyConfigForWidth(w)
    ensureFBO(canvas.width, canvas.height)
    ensureColorFBO(canvas.width, canvas.height)
  }

  const parallelExt = gl.getExtension('KHR_parallel_shader_compile')
  function checkLinkStatus() {
    if (!mainProgram) return
    const programs = [mainProgram, prepassProgram, postProgram].filter(Boolean)
    if (parallelExt) {
      for (const p of programs) {
        if (!gl.getProgramParameter(p, parallelExt.COMPLETION_STATUS_KHR)) {
          setTimeout(checkLinkStatus, 0)
          return
        }
      }
    }
    for (const p of programs) {
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        console.error(gl.getProgramInfoLog(p))
      }
    }
  }

  resize()
  checkLinkStatus()

  const onLost = (e) => {
    e.preventDefault()
    onFail?.()
  }
  const onRestored = () => {
    location.reload()
  }
  canvas.addEventListener('webglcontextlost', onLost)
  canvas.addEventListener('webglcontextrestored', onRestored)

  let resizeTimer
  const onResize = () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(resize, 10)
  }
  window.addEventListener('resize', onResize)

  const onVisibility = () => {
    if (document.visibilityState === 'visible') dirty = true
  }
  document.addEventListener('visibilitychange', onVisibility)

  function renderFrame() {
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
    gl.viewport(0, 0, fboWidth, fboHeight)
    useProgram(prepassProgram)
    setPrepassUniforms(gl, prepassUniforms, fboWidth, fboHeight, state)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

    gl.bindFramebuffer(gl.FRAMEBUFFER, colorFbo)
    gl.viewport(0, 0, canvas.width, canvas.height)
    useProgram(mainProgram)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, fboTex)
    gl.uniform1i(mainUniforms.u_noiseTex, 0)
    gl.uniform2f(mainUniforms.u_noiseTexSize, fboWidth, fboHeight)
    setUniforms(gl, mainUniforms, canvas, state)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    gl.viewport(0, 0, canvas.width, canvas.height)
    useProgram(postProgram)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, colorTex)
    gl.uniform1i(postUniforms.u_colorTex, 0)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, fboTex)
    gl.uniform1i(postUniforms.u_noiseTex, 1)
    gl.uniform2f(postUniforms.u_noiseTexSize, fboWidth, fboHeight)
    setPostUniforms(gl, postUniforms, canvas, state, BLUR_RADIUS)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
  }

  let raf = 0
  let stopped = false

  function frame(timestamp) {
    if (stopped) return
    if (document.visibilityState === 'hidden') {
      raf = requestAnimationFrame(frame)
      return
    }
    if (tick(state, timestamp)) dirty = true
    if (dirty) {
      renderFrame()
      dirty = false
    }
    raf = requestAnimationFrame(frame)
  }
  raf = requestAnimationFrame(frame)

  return function cleanup() {
    stopped = true
    cancelAnimationFrame(raf)
    clearTimeout(resizeTimer)
    window.removeEventListener('resize', onResize)
    document.removeEventListener('visibilitychange', onVisibility)
    canvas.removeEventListener('webglcontextlost', onLost)
    canvas.removeEventListener('webglcontextrestored', onRestored)
    reducedMotion?.removeEventListener?.('change', onReducedChange)
    if (mainProgram) gl.deleteProgram(mainProgram)
    if (prepassProgram) gl.deleteProgram(prepassProgram)
    if (postProgram) gl.deleteProgram(postProgram)
    if (fbo) gl.deleteFramebuffer(fbo)
    if (fboTex) gl.deleteTexture(fboTex)
    if (colorFbo) gl.deleteFramebuffer(colorFbo)
    if (colorTex) gl.deleteTexture(colorTex)
    gl.deleteBuffer(buf)
  }
}
